// src/services/cloudinaryService.js
// ======================================================
// ☁️ CLOUDINARY SERVICE - VERSIÓN ULTRA RÁPIDA
// ======================================================
// ✅ Subida directa a Cloudinary
// ✅ Subida PARALELA (múltiples imágenes simultáneas)
// ✅ Retry automático en fallos
// ✅ Timeout configurable
// ✅ Transformaciones CDN automáticas
// ✅ Validación de tamaño y formato
// ======================================================

import axios from 'axios';
import { CLOUDINARY_CONFIG } from '../config/imageConfig';

// ======================================================
// ⚙️ CONFIGURACIÓN
// ======================================================

const UPLOAD_CONFIG = {
  parallelUploads: 3,        // Subir 3 imágenes simultáneamente
  maxRetries: 2,             // Reintentar 2 veces si falla
  retryDelay: 1000,          // 1 segundo entre reintentos
  timeout: 60000,            // 60 segundos máximo por upload
  maxFileSize: 10 * 1024 * 1024, // 10MB
};

// ======================================================
// ☁️ SUBIR UNA IMAGEN A CLOUDINARY
// ======================================================

/**
 * Sube una imagen a Cloudinary con retry automático
 * @param {File} file - Archivo de imagen
 * @param {string} propertyId - ID de la propiedad
 * @param {string} userId - ID del usuario
 * @param {Function} onProgress - Callback de progreso (0-100)
 * @param {number} retryCount - Contador interno de reintentos
 * @returns {Promise<Object>} - URL y metadatos de la imagen
 */
export async function uploadToCloudinary(file, propertyId, userId, onProgress, retryCount = 0) {
  try {
    // Validar tamaño
    const maxSize = CLOUDINARY_CONFIG.maxFileSize || UPLOAD_CONFIG.maxFileSize;
    if (file.size > maxSize) {
      throw new Error(`Arquivo muito grande. Máximo: ${maxSize / 1024 / 1024}MB`);
    }

    // Validar formato
    const allowedFormats = CLOUDINARY_CONFIG.allowedFormats || ['jpg', 'jpeg', 'png', 'webp', 'avif'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedFormats.includes(fileExtension)) {
      throw new Error(`Formato não suportado. Use: ${allowedFormats.join(', ')}`);
    }

    // Crear FormData
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_CONFIG.uploadPreset);
    formData.append('folder', `${CLOUDINARY_CONFIG.folder}/${userId}/${propertyId}`);
    formData.append('public_id', `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    
    // ✅ Transformaciones automáticas en CDN (calidad optimizada)
    formData.append('quality', 'auto:best');
    formData.append('fetch_format', 'auto');

    // Subir a Cloudinary
    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CONFIG.cloudName}/image/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: UPLOAD_CONFIG.timeout,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        }
      }
    );

    const result = response.data;
    
    console.log(`✅ Cloudinary: ${result.secure_url.substring(0, 50)}...`);
    
    return {
      url: result.secure_url,
      publicId: result.public_id,
      format: result.format,
      bytes: result.bytes,
      width: result.width,
      height: result.height,
      createdAt: new Date().toISOString(),
      // URL optimizada para diferentes tamaños
      thumbnail: result.secure_url.replace('/upload/', '/upload/w_400,h_400,c_fill,q_auto:good/'),
      preview: result.secure_url.replace('/upload/', '/upload/w_800,h_800,c_fill,q_auto:best/'),
    };
    
  } catch (error) {
    console.error(`❌ Erro Cloudinary (tentativa ${retryCount + 1}):`, error.message);
    
    // Retry automático
    if (retryCount < UPLOAD_CONFIG.maxRetries) {
      console.log(`🔄 Retentando upload... (${retryCount + 1}/${UPLOAD_CONFIG.maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, UPLOAD_CONFIG.retryDelay * (retryCount + 1)));
      return uploadToCloudinary(file, propertyId, userId, onProgress, retryCount + 1);
    }
    
    throw new Error(error.response?.data?.error?.message || error.message || 'Falha ao subir imagem');
  }
}

// ======================================================
// 🚀 SUBIR MÚLTIPLES IMÁGENES (PARALELO)
// ======================================================

/**
 * Sube múltiples imágenes a Cloudinary EN PARALELO
 * @param {File[]} files - Array de archivos
 * @param {string} propertyId - ID de la propiedad
 * @param {string} userId - ID del usuario
 * @param {Function} onProgress - Callback de progreso detallado
 * @returns {Promise<Array>} - Array de URLs
 */
export async function uploadMultipleToCloudinary(files, propertyId, userId, onProgress) {
  if (!files || files.length === 0) return [];
  
  const total = files.length;
  const results = new Array(total).fill(null);
  const errors = [];
  let completed = 0;
  
  // ✅ SUBIDA PARALELA (3 imágenes a la vez)
  const parallelLimit = UPLOAD_CONFIG.parallelUploads;
  
  for (let i = 0; i < total; i += parallelLimit) {
    const batch = files.slice(i, i + parallelLimit);
    const batchIndices = batch.map((_, idx) => i + idx);
    
    if (onProgress) {
      onProgress({
        current: completed,
        total,
        percent: Math.round((completed / total) * 100),
        stage: 'uploading',
        uploading: `Lote ${Math.floor(i / parallelLimit) + 1}/${Math.ceil(total / parallelLimit)}`
      });
    }
    
    // Subir lote en paralelo
    const batchPromises = batch.map(async (file, batchIndex) => {
      const globalIndex = i + batchIndex;
      
      try {
        const result = await uploadToCloudinary(
          file, 
          propertyId, 
          userId, 
          (percent) => {
            if (onProgress) {
              // Progreso individual dentro del lote
              onProgress({
                current: completed,
                total,
                percent: Math.round(((completed + (percent / 100)) / total) * 100),
                stage: 'uploading',
                uploading: file.name,
                individualPercent: percent
              });
            }
          }
        );
        
        results[globalIndex] = result.url;
        completed++;
        
        if (onProgress) {
          onProgress({
            current: completed,
            total,
            percent: Math.round((completed / total) * 100),
            stage: completed === total ? 'complete' : 'uploading',
            completed: results.filter(Boolean).length,
            uploading: null
          });
        }
        
        return { success: true, url: result.url, index: globalIndex };
        
      } catch (error) {
        console.error(`❌ Erro na imagem ${globalIndex} (${file.name}):`, error.message);
        errors.push({ index: globalIndex, file: file.name, error: error.message });
        
        if (onProgress) {
          onProgress({
            current: completed,
            total,
            percent: Math.round((completed / total) * 100),
            stage: 'error',
            error: error.message,
            failed: file.name
          });
        }
        
        return { success: false, error: error.message, index: globalIndex };
      }
    });
    
    await Promise.all(batchPromises);
  }
  
  const validUrls = results.filter(Boolean);
  console.log(`⚡ ${validUrls.length}/${total} imagens enviadas para Cloudinary`);
  
  if (errors.length > 0) {
    console.warn(`⚠️ ${errors.length} imagens falharam:`, errors.map(e => e.file));
  }
  
  return validUrls;
}

// ======================================================
// 🗑️ ELIMINAR IMAGEN DE CLOUDINARY (OPCIONAL)
// ======================================================

/**
 * Elimina una imagen de Cloudinary por su public_id
 * @param {string} publicId - ID público de la imagen en Cloudinary
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export async function deleteFromCloudinary(publicId) {
  try {
    // Nota: Para eliminar se requiere API Key y Secret
    // Esta función está preparada para cuando se añadan las credenciales
    console.warn('⚠️ deleteFromCloudinary requiere API Key y Secret');
    return false;
  } catch (error) {
    console.error('❌ Erro ao eliminar do Cloudinary:', error);
    return false;
  }
}

// ======================================================
// 🔍 OBTENER INFORMACIÓN DE IMAGEN
// ======================================================

/**
 * Obtiene información de una imagen en Cloudinary
 * @param {string} publicId - ID público de la imagen
 * @returns {Promise<Object>} - Información de la imagen
 */
export async function getImageInfo(publicId) {
  try {
    const url = `https://res.cloudinary.com/${CLOUDINARY_CONFIG.cloudName}/image/upload/${publicId}`;
    // Head request para obtener información sin descargar
    const response = await fetch(url, { method: 'HEAD' });
    return {
      exists: response.ok,
      url,
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length'),
    };
  } catch (error) {
    console.error('❌ Erro ao obter informação:', error);
    return { exists: false, error: error.message };
  }
}

// ======================================================
// 🎨 GENERAR URL OPTIMIZADA
// ======================================================

/**
 * Genera una URL optimizada de Cloudinary con transformaciones
 * @param {string} url - URL original de Cloudinary
 * @param {Object} options - Opciones de transformación
 * @returns {string} - URL optimizada
 */
export function getOptimizedUrl(url, options = {}) {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  
  const {
    width = 800,
    height = 800,
    quality = 'auto:good',
    format = 'auto',
    crop = 'fill'
  } = options;
  
  const transformation = `w_${width},h_${height},c_${crop},q_${quality},f_${format}`;
  return url.replace('/upload/', `/upload/${transformation}/`);
}

// ======================================================
// 📤 EXPORTS
// ======================================================

export default {
  uploadToCloudinary,
  uploadMultipleToCloudinary,
  deleteFromCloudinary,
  getImageInfo,
  getOptimizedUrl,
};